import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Mic, MicOff, Settings, Video, VideoOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isPseudoDevice } from "../lib/callDevices";

export interface MediaDeviceOption {
  deviceId: string;
  label: string;
}

interface DeviceSelectButtonProps {
  kind: "microphone" | "camera";
  on: boolean;
  onToggle: () => void;
  deviceId: string | null;
  onSelectDevice: (deviceId: string) => void;
  /** Device list owned by the caller (GH-1859 `useCallDevices`). Without it
   *  the button enumerates on open, as the pre-join screens still do. */
  devices?: MediaDeviceOption[];
  /** Called as the menu opens — lets the owner of `devices` re-read them. */
  onOpen?: () => void;
  /** Heading over the device list — defaults to the singular kind label. */
  sectionLabel?: string;
  /** Extra menu content under the device list (the mic's Speakers section).
   *  Receives `close` so a pick inside it can dismiss the menu. */
  extraSection?: (close: () => void) => ReactNode;
  /** Open the menu as a bottom sheet (phones) instead of an anchored popover. */
  sheet?: boolean;
  /** Larger 56px variant used on the big pre-join/waiting-room preview tiles. */
  big?: boolean;
  /** Open the device list upward — used when the button sits near the bottom
   *  of its container (pre-join / waiting-room preview tiles). */
  openUp?: boolean;
  onOpenSettings?: () => void;
  /** Light variant for page-level surfaces (e.g. the in-call bottom bar,
   *  which sits on `bg-background` — not the black/white preview tile the
   *  default styling assumes). Matches the design's `DeviceControl light`
   *  prop: a bordered `bg-foreground/[0.06]` button and a `bg-background`
   *  dropdown panel, instead of the dark preview-tile treatment. */
  light?: boolean;
  /** Round 44px control used by the in-call bar. */
  circle?: boolean;
  /** 44px squircle — the design's phone call bar (`.ic-bar .w-12` → 2.75rem). */
  small?: boolean;
  /** 36px circle + a chevron that sits on the button (not in the gap),
   *  so a tight mobile bar never stacks badges onto the next control. */
  compact?: boolean;
}

/**
 * Mic/camera toggle button with a small chevron badge that opens a real
 * device picker. In a call and on the pre-join screens the caller passes the
 * device list and, for the mic, the Speakers section (GH-1859); without a
 * list the button enumerates on open.
 * Chrome's "Default" / "Communications" pseudo-entries are never listed.
 */
export function DeviceSelectButton({
  kind,
  on,
  onToggle,
  deviceId,
  onSelectDevice,
  devices: controlledDevices,
  onOpen,
  sectionLabel,
  extraSection,
  sheet,
  big,
  openUp,
  onOpenSettings,
  light,
  circle,
  small,
  compact,
}: DeviceSelectButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [enumerated, setEnumerated] = useState<MediaDeviceOption[]>([]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const devices = controlledDevices ?? enumerated;
  const close = () => setOpen(false);

  const kindLabel = kind === "microphone" ? t("call.preJoin.microphone") : t("call.preJoin.camera");
  const IconOn = kind === "microphone" ? Mic : Video;
  const IconOff = kind === "microphone" ? MicOff : VideoOff;

  useEffect(() => {
    if (!open || controlledDevices) return;
    let cancelled = false;
    const kindFilter = kind === "microphone" ? "audioinput" : "videoinput";
    navigator.mediaDevices
      .enumerateDevices()
      .then((list) => {
        if (cancelled) return;
        const opts = list
          .filter((d) => d.kind === kindFilter && !isPseudoDevice(d))
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `${kindLabel} ${i + 1}` }));
        setEnumerated(opts);
      })
      .catch(() => {
        if (!cancelled) setEnumerated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, kindLabel, controlledDevices]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node)) return;
      if (wrapRef.current?.contains(e.target)) return;
      if (sheetRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  const sizeCls = big ? "size-14" : compact ? "size-9" : circle || small ? "size-11" : "size-12";
  const iconCls = big ? "size-6" : compact ? "size-4" : "size-5";
  const radiusCls = circle || compact ? "rounded-full" : "rounded-2xl";
  const menuLabel = extraSection ? t("call.controls.micAndSpeakers") : kindLabel;

  const menuBody = (
    <>
      <DeviceMenuHeading light={light}>{sectionLabel ?? kindLabel}</DeviceMenuHeading>
      {devices.length === 0 ? (
        <div className={`px-3 py-2 text-sm ${light ? "text-muted-foreground/70" : "text-white/50"}`}>…</div>
      ) : (
        devices.map((d) => (
          <DeviceMenuItem
            key={d.deviceId}
            label={d.label}
            selected={d.deviceId === deviceId}
            icon={IconOn}
            light={light}
            onClick={() => {
              onSelectDevice(d.deviceId);
              close();
            }}
          />
        ))
      )}
      {extraSection?.(close)}
      {onOpenSettings && (
        <>
          <DeviceMenuDivider light={light} />
          <button
            type="button"
            onClick={() => {
              close();
              onOpenSettings();
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start transition-colors ${
              light ? "text-muted-foreground hover:bg-muted" : "text-white/60 hover:bg-white/10"
            }`}
          >
            <Settings className="size-[15px]" /> {t("call.preJoin.audioVideoSettings")}
          </button>
        </>
      )}
    </>
  );

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        title={
          on
            ? kind === "microphone"
              ? t("call.controls.muteMic")
              : t("call.controls.turnOffCamera")
            : kind === "microphone"
              ? t("call.controls.unmuteMic")
              : t("call.controls.turnOnCamera")
        }
        aria-label={
          on
            ? kind === "microphone"
              ? t("call.controls.muteMic")
              : t("call.controls.turnOffCamera")
            : kind === "microphone"
              ? t("call.controls.unmuteMic")
              : t("call.controls.turnOnCamera")
        }
        className={`${sizeCls} ${radiusCls} flex items-center justify-center transition-colors ${
          // Default (dark) sits on a `bg-foreground` preview tile (flips
          // black/white with the theme — see PreJoinView), so the "on" state
          // derives from `background`, the guaranteed opposite of
          // `foreground` in both themes, instead of a literal white that
          // goes invisible in dark mode (a white-on-white button — the tile
          // is white there too). `light` sits on a page-level `bg-background`
          // surface (the in-call bar) and uses the design's bordered
          // `bg-foreground/[0.06]` treatment instead.
          on
            ? light
              ? "bg-foreground/[0.06] text-foreground border border-border hover:bg-foreground/10"
              : "bg-background/15 text-background hover:bg-background/25"
            : "bg-red-500 text-white hover:bg-red-600"
        }`}
      >
        {on ? <IconOn className={iconCls} /> : <IconOff className={iconCls} />}
      </button>
      <button
        type="button"
        onClick={() => {
          if (!open) onOpen?.();
          setOpen(!open);
        }}
        title={t("call.preJoin.changeDevice")}
        aria-label={t("call.preJoin.changeDevice")}
        aria-expanded={open}
        className={`absolute rounded-full border-2 flex items-center justify-center transition-colors z-10 ${
          compact || circle ? "top-0 end-0 size-3.5" : "-top-1.5 -right-1.5 size-5"
        } ${light ? "border-background" : "border-foreground"} ${
          open
            ? "bg-primary text-primary-foreground"
            : light
              ? "bg-muted-foreground/70 text-white hover:bg-muted-foreground"
              : "bg-neutral-700 text-white hover:bg-neutral-600"
        }`}
      >
        <ChevronDown className={compact || circle ? "size-2.5" : "size-[11px]"} />
      </button>
      {open &&
        (sheet ? (
          // Portaled: an ancestor transform would otherwise become the
          // containing block for the sheet's `fixed inset-0`.
          createPortal(
            <div className="fixed inset-0 z-40 flex items-end justify-center px-3 pb-24">
              <button
                type="button"
                className="absolute inset-0 bg-foreground/20"
                aria-label={t("call.controls.sheetClose")}
                onClick={close}
              />
              <div
                ref={sheetRef}
                role="menu"
                aria-label={menuLabel}
                className={`relative w-full max-w-sm max-h-[70vh] overflow-y-auto rounded-3xl shadow-2xl z-10 p-1.5 ${
                  light ? "bg-background border border-border" : "bg-neutral-900 border border-white/10"
                }`}
              >
                {menuBody}
              </div>
            </div>,
            document.body,
          )
        ) : (
          <div
            role="menu"
            aria-label={menuLabel}
            className={`absolute z-50 ${extraSection ? "w-72" : "w-60"} max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl py-2 left-1/2 -translate-x-1/2 anim-scale-in ${
              light ? "bg-background border border-border" : "bg-neutral-900 border border-white/10"
            } ${openUp ? "bottom-full mb-3" : "top-full mt-3"}`}
          >
            {menuBody}
          </div>
        ))}
    </div>
  );
}

/** Section heading inside a device menu. */
export function DeviceMenuHeading({ light, children }: { light?: boolean; children: ReactNode }) {
  return (
    <div
      className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
        light ? "text-muted-foreground" : "text-white/40"
      }`}
    >
      {children}
    </div>
  );
}

export function DeviceMenuDivider({ light }: { light?: boolean }) {
  return <div className={`h-px my-1 mx-2 ${light ? "bg-border" : "bg-white/10"}`} />;
}

/** One selectable device row; a check marks the selected one. */
export function DeviceMenuItem({
  label,
  selected,
  icon: Icon,
  light,
  onClick,
}: {
  label: string;
  selected: boolean;
  icon: LucideIcon;
  light?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-start transition-colors ${
        light ? "text-foreground hover:bg-muted" : "text-white/90 hover:bg-white/10"
      }`}
    >
      {selected ? (
        <Check className="size-[15px] text-primary shrink-0" />
      ) : (
        <Icon className={`size-[15px] shrink-0 ${light ? "text-muted-foreground/60" : "text-white/40"}`} />
      )}
      <span className={`flex-1 truncate ${selected ? "font-bold" : ""}`}>{label}</span>
    </button>
  );
}
